import { Injectable, Provider } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { BehaviorSubject, Observable, delay, map, of, switchMap } from 'rxjs';
import { RequiredFields } from '../models/RequiredFields';
import { CreateLeadResponse, InternalLeadConfirmationModel, InternalLeadHeaderModel, InternalLeadSetupModel, InternalReferral, InternalReferringSource, InvolvedParty, localStorageIntoDB, ProviderList, ReferralCodeVerification, StateCounty, StateNames } from '../models/vendor.model';
import { Questions } from '../models/Questions';
import { environment } from 'src/environments/environment';
import { InternalSubjectRequiredFieldsModel } from '../models/InternalSubjectRequiredFieldsModel';
import { Subject } from 'rxjs';
import { LOADING_ELEMENT } from './components/loading-spinner/loading-element.token';

export interface ValidateCodeResponse {
  IsValid?: boolean;
  Message?: string;
  isValid?: boolean;
  message?: string;
}

@Injectable({
  providedIn: 'root'
})
export class VendorService {

  baseUrl = environment.baseUri;

  private fileUploadStartSubject: Subject<any> = new Subject<any>();
  private fileUploadEndSubject: Subject<any> = new Subject<any>();
  private fileUploadDataSubject: Subject<any> = new Subject<any>();
  uploadFiles: string[] = [];
  private storageKey = 'activeformdata';
  private dataSubject = new Subject<any>();
  filesReadyForUpload: any = [];
  FilesToUpload: any = [];
  
  constructor(private http: HttpClient) { }

  public getStoredInstanceNumber(): number {
    const raw = sessionStorage.getItem('instanceNumber');
    const parsed = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(parsed) && parsed >= 1 && parsed <= 255 ? parsed : 0;
  }

  getFormData(): any {
    this.dataSubject.observers = [];
    return this.dataSubject.asObservable();
  }

  updateData(newData: any): void {
    sessionStorage.setItem(this.storageKey, JSON.stringify(newData));
    this.dataSubject.next(JSON.parse(JSON.stringify(newData)));
  }

  getSessionData() {
    return JSON.parse(sessionStorage.getItem("activeformdata") ?? "{}");
  }

  sendUploadStartData(data: any) {
    this.fileUploadStartSubject.next(data);
  }

  getUploadStartData(): Observable<any> {
    this.fileUploadStartSubject.observers = [];
    return this.fileUploadStartSubject.asObservable();
  }

  sendUploadEndData(data: any) {
    this.fileUploadEndSubject.next(data);
  }

  getUploadEndData(): Observable<any> {
    this.fileUploadEndSubject.observers = [];
    return this.fileUploadEndSubject.asObservable();
  }

  sendUploadFileData(data: any) {
    this.fileUploadDataSubject.next(data);
  }

  getUploadFileData(): Observable<any> {
    this.fileUploadDataSubject.observers = [];
    return this.fileUploadDataSubject.asObservable();
  }

  public getPayorIdByCode(securityCode: string | null): Observable<ReferralCodeVerification> {
    var contextDetails = new HttpContext().set(LOADING_ELEMENT, 'body');
    return this.http.get<ReferralCodeVerification>(this.baseUrl + "FCReferral/GetPayorIdByCode?securityCode=" + securityCode, { context: contextDetails });
  }

  public getRequiredFields(payorkey: number, instanceNumber: number): Observable<RequiredFields> {
    var contextDetails = new HttpContext().set(LOADING_ELEMENT, 'body');
    return this.http.get<RequiredFields>(this.baseUrl + "FCReferral/GetRequiredFields?payorKey=" + payorkey + "&instanceNumber=" + instanceNumber, { context: contextDetails });
  }

  public getLeadPatientsTerm(payorkey: number, instanceNumber: number): Observable<any> {
    var contextDetails = new HttpContext().set(LOADING_ELEMENT, 'body');
    return this.http.get<any>(this.baseUrl + "FCReferral/GetLeadPatientsTerm?payorKey=" + payorkey + "&instanceNumber=" + instanceNumber, { context: contextDetails });
  }

  public getInvolvedPartiesLookup(payorkey: number, instanceNumber: number): Observable<InvolvedParty[]> {
    var contextDetails = new HttpContext().set(LOADING_ELEMENT, 'body');
    return this.http.get<InvolvedParty[]>(this.baseUrl + "FCReferral/GetInvolvedPartiesLookup?payorKey=" + payorkey + "&instanceNumber=" + instanceNumber, { context: contextDetails });
  }


  public getReferralTypesLookup(payorkey: number, instanceNumber: number): Observable<InternalReferral[]> {
    var contextDetails = new HttpContext().set(LOADING_ELEMENT, 'body');
    return this.http.get<InternalReferral[]>(this.baseUrl + "FCReferral/GetReferralTypesLookup?payorKey=" + payorkey + "&instanceNumber=" + instanceNumber, { context: contextDetails });
  }

  public getReferringSourcesLookup(payorkey: number, instanceNumber: number): Observable<InternalReferringSource[]> {
    var contextDetails = new HttpContext().set(LOADING_ELEMENT, 'body');
    return this.http.get<InternalReferringSource[]>(this.baseUrl + "FCReferral/GetReferringSourcesLookup?payorKey=" + payorkey + "&instanceNumber=" + instanceNumber, { context: contextDetails });
  }

  public getStateNames(): Observable<StateNames[]> {
    var contextDetails = new HttpContext().set(LOADING_ELEMENT, 'body');
    return this.http.get<StateNames[]>(this.baseUrl + "FCReferral/GetStates", { context: contextDetails });
  }
  public getStateCounties(isocode: string): Observable<StateCounty[]> {
    var contextDetails = new HttpContext().set(LOADING_ELEMENT, 'body');
    return this.http
      .get<StateCounty[]>(this.baseUrl + "FCReferral/GetStateCounties?isocode=" + isocode, { context: contextDetails })
      .pipe(
        map((counties) =>
          [...(counties ?? [])].sort((a, b) =>
            (a?.County ?? '').localeCompare(b?.County ?? '', undefined, { sensitivity: 'base' })
          )
        )
      );
  }
  public getLeadHeaders(payorkey: number, instanceNumber: number): Observable<InternalLeadHeaderModel> {
    var contextDetails= new HttpContext().set(LOADING_ELEMENT, 'body');
    return this.http.get<InternalLeadHeaderModel>(this.baseUrl + "FCReferral/GetLeadHeaders?payorKey=" + payorkey + "&instanceNumber=" + instanceNumber, { context: contextDetails });
  }

  public getLeadQuestion(payorkey: number, instanceNumber: number): Observable<any> {
    var contextDetails = new HttpContext().set(LOADING_ELEMENT, 'body');
    return this.http.get<any>(this.baseUrl + "FCReferral/GetLeadQuestion?payorKey=" + payorkey + "&instanceNumber=" + instanceNumber, { context: contextDetails });
  }

  public getLeadConfirmation(payorkey: number, instanceNumber: number): Observable<InternalLeadConfirmationModel> {
    var contextDetails = new HttpContext().set(LOADING_ELEMENT, 'body');
    return this.http.get<InternalLeadConfirmationModel>(this.baseUrl + "FCReferral/GetLeadConfirmation?payorKey=" + payorkey + "&instanceNumber=" + instanceNumber, { context: contextDetails });
  }

  public createLead(payorkey: number, data: any, instanceNumber: number): Observable<CreateLeadResponse> {
    var contextDetails = new HttpContext().set(LOADING_ELEMENT, 'body');
    return this.http.post<CreateLeadResponse>(this.baseUrl + "FCReferral/CreateLead?payorKey=" + payorkey + "&instanceNumber=" + instanceNumber, data, { context: contextDetails });
  }

  public getLookUpProviderSpeciality(payorkey: number,): Observable<ProviderList[]> {
    var contextDetails = new HttpContext().set(LOADING_ELEMENT, 'body');
    return this.http.get<ProviderList[]>(this.baseUrl + "FCReferral/GetLookUpProviderSpeciality?payorKey=" + payorkey, { context: contextDetails });
  }
  public getLookUpProviderType(payorkey: number,): Observable<ProviderList[]> {
    var contextDetails = new HttpContext().set(LOADING_ELEMENT, 'body');
    return this.http.get<ProviderList[]>(this.baseUrl + "FCReferral/GetLookUpProviderType?payorKey=" + payorkey, { context: contextDetails });
  }

  public getLeadSetup(payorkey: number, instanceNumber: number): Observable<InternalLeadSetupModel> {
    var contextDetails = new HttpContext().set(LOADING_ELEMENT, 'body');
    return this.http.get<InternalLeadSetupModel>(this.baseUrl + "FCReferral/GetLeadSetup?payorKey=" + payorkey + "&instanceNumber=" + instanceNumber, { context: contextDetails });
  }

  public getSubjectRequiredFieldsByInvolvedParty(internalInvolvedPartiesKey: number, payorkey: number, instanceNumber: number): Observable<InternalSubjectRequiredFieldsModel> {
    var contextDetails = new HttpContext().set(LOADING_ELEMENT, 'body');
    return this.http.get<InternalSubjectRequiredFieldsModel>(this.baseUrl + "FCReferral/GetSubjectRequiredFieldsByInvolvedParty?payorKey=" + payorkey + "&internalInvolvedPartiesKey=" + internalInvolvedPartiesKey + "&instanceNumber=" + instanceNumber, { context: contextDetails });
  }

  public getInvolvedPartyById(internalInvolvedPartiesKey: number, payorkey: number, instanceNumber: number): Observable<InvolvedParty> {
    var contextDetails = new HttpContext().set(LOADING_ELEMENT, 'body');
    return this.http.get<InvolvedParty>(this.baseUrl + "FCReferral/GetInvolvedPartyById?payorKey=" + payorkey + "&internalInvolvedPartiesKey=" + internalInvolvedPartiesKey + "&instanceNumber=" + instanceNumber, { context: contextDetails });
  }

  public addAttachmentToDB(data: any | null): Observable<number> {
    var contextDetails = new HttpContext().set(LOADING_ELEMENT, 'body');
    return this.http.post<number>(this.baseUrl + "FCReferral/AddAttachmentToDB", data, { context: contextDetails });
  }

  public commitLargeFiles(attachmentKey: any | null, fileName: any, totalChunks: any): Observable<number> {
    var contextDetails = new HttpContext().set(LOADING_ELEMENT, 'body');
    return this.http.get<number>(this.baseUrl + "FCReferral/CommitFile?attachmentKey=" + attachmentKey + "&fileName=" + fileName + "&totalChunks=" + totalChunks, { context: contextDetails });
  }

  public GetAttachmentStatus(data: any | null): Observable<any> {
    var contextDetails = new HttpContext().set(LOADING_ELEMENT, 'body');
    return this.http.post<any>(this.baseUrl + "FCReferral/GetAttachmentStatus", data, { context: contextDetails });
  }

  public pollStatus(interval: number, data: any | null): Observable<any> {
    return this.GetAttachmentStatus(data).pipe(switchMap(status => {
      if (status.filter((x: any) => x.VirusScanStatus == null || x.VirusScanStatus == 0).length == 0) {
        return of(status);
      }
      else {
        return of(status).pipe(delay(interval), switchMap(() => this.pollStatus(interval, data)));
      }
    }));
  }

  public generateCode(contactEmail: string | null, applicationCode: string | null, instanceNumber: number = this.getStoredInstanceNumber()) {
    var contextDetails = new HttpContext().set(LOADING_ELEMENT, 'body');
    return this.http.post<{ code: string, message: string }>(this.baseUrl + "EmailAuth/GenerateCode", { contactEmail, applicationCode, instanceNumber }, { context: contextDetails });
  }

  public validateCode(data: { authCode: string; email: string; applicationCode: string; instanceNumber?: number }): Observable<ValidateCodeResponse | boolean> {
    var contextDetails = new HttpContext().set(LOADING_ELEMENT, 'body');
    const payload = { ...data, instanceNumber: data.instanceNumber ?? this.getStoredInstanceNumber() };
    return this.http.post<ValidateCodeResponse | boolean>(this.baseUrl + "EmailAuth/ValidateCode", payload, { context: contextDetails });
  }

  public verifyReferralCode(securityCode: string | null): Observable<ReferralCodeVerification> {
    var contextDetails = new HttpContext().set(LOADING_ELEMENT, 'body');
    return this.http.get<ReferralCodeVerification>(this.baseUrl + "FCReferral/VerifyReferralCode?securityCode=" + securityCode, { context: contextDetails });
  }

  public sendReferralEmail(data:any): Observable<any> {
    var contextDetails = new HttpContext().set(LOADING_ELEMENT, 'body');
    return this.http.post<any>(this.baseUrl + "FCReferral/SendReferralEmail", data, { context: contextDetails });
  }

}

